import CodesPanel from './CodesPanel.vue';
import CodeEditPanel from './CodeEditPanel.vue';
import { generateCodeSvg, normalizeCodeItem, type CodeDesignItem } from './codeSvg';
import { normalizeColorPolicy, type BrandColor, type ColorPickerMode } from '../../../src/shared/brandPalette';
import { t } from './i18n';

type RenderResult = { component: unknown; props?: Record<string, unknown> };
type VeyopFacade = {
  hooks?: {
    on?: (event: string, handler: (payload?: unknown) => void) => unknown;
    whenReady?: (surface: 'designer', handler: (payload?: unknown) => void) => unknown;
    registerSlot?: (name: string, namespace: string, render: (context: Record<string, unknown>) => RenderResult | null, priority?: number) => () => void;
  };
  itemRenderers?: { register?: (entry: unknown) => unknown };
  panels?: { register?: (entry: unknown) => unknown };
  itemBounds?: { register?: (entry: unknown) => unknown };
  imagePipeline?: {
    registerItemSvgExporter?: (entry: unknown) => unknown;
    registerImageRasterizer?: (entry: unknown) => unknown;
  };
  designer?: {
    registerLeftPanel?: (entry: unknown) => unknown;
  };
};

let registered = false;
let colorPickerMode: ColorPickerMode = 'system';
let brandColors: BrandColor[] = [];

function colorProps() {
  return { colorPickerMode, brandColors };
}

function isCodeItem(item: CodeDesignItem | null | undefined): boolean {
  return item?.type === 'qrcode' || item?.type === 'barcode';
}

function renderCodeItem(item: CodeDesignItem, container: HTMLElement): () => void {
  // Regenerate rather than reuse `item.svg`: the cached markup was sized for the box before
  // the last resize, and a barcode is now drawn to fill its exact box.
  const svg = generateCodeSvg(item).svg;
  container.style.display = 'block';
  container.style.width = '100%';
  container.style.height = '100%';
  container.innerHTML = svg;
  const child = container.querySelector('svg');
  child?.setAttribute('width', '100%');
  child?.setAttribute('height', '100%');
  if (child && !child.hasAttribute('preserveAspectRatio')) child.setAttribute('preserveAspectRatio', 'xMidYMid meet');
  return () => {
    container.replaceChildren();
  };
}

function exportCodeSvg(item: CodeDesignItem): string {
  return generateCodeSvg(item).svg;
}

function rasterizeSvg(svg: string, width: number, height: number): Promise<string | null> {
  return new Promise((resolve) => {
    const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(width));
        canvas.height = Math.max(1, Math.round(height));
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(null);
          return;
        }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/png'));
      } catch {
        resolve(null);
      } finally {
        URL.revokeObjectURL(url);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}

function registerOnVeyop(Veyop: VeyopFacade): void {
  if (registered) return;
  if (!Veyop.itemRenderers?.register && !Veyop.designer?.registerLeftPanel) return;
  registered = true;

  Veyop.itemRenderers?.register?.({
    id: 'qr-barcode',
    priority: 35,
    matches: isCodeItem,
    render: renderCodeItem,
  });

  Veyop.itemBounds?.register?.({
    id: 'qr-barcode',
    priority: 35,
    matches: isCodeItem,
    measure: (item: CodeDesignItem) => {
      const normalized = normalizeCodeItem(item);
      return { width: normalized.width, height: normalized.height };
    },
  });

  Veyop.panels?.register?.({
    id: 'qr-barcode',
    label: t('Code'),
    description: t('Edit QR and barcode data'),
    priority: 35,
    matches: (item: CodeDesignItem | null) => isCodeItem(item),
    render: (ctx: { item: CodeDesignItem | null; update: (patch: Record<string, unknown>) => void }) => ({
      component: CodeEditPanel,
      props: { item: ctx.item, update: ctx.update, ...colorProps() },
    }),
  });

  Veyop.imagePipeline?.registerItemSvgExporter?.({
    id: 'qr-barcode',
    priority: 35,
    matches: isCodeItem,
    exportSvg: exportCodeSvg,
  });

  Veyop.imagePipeline?.registerImageRasterizer?.({
    id: 'qr-barcode',
    priority: 35,
    matches: isCodeItem,
    rasterize: async (item: CodeDesignItem, options: { width: number; height: number }) =>
      rasterizeSvg(exportCodeSvg(item), options.width, options.height),
  });

  Veyop.designer?.registerLeftPanel?.({
    id: 'qr-barcode',
    group: 'graphics',
    label: t('Codes'),
    description: t('Create QR codes and barcodes'),
    icon: {
      type: 'material',
      path: 'M120-520v-320h320v320H120Zm80-80h160v-160H200v160Zm-80 480v-320h320v320H120Zm80-80h160v-160H200v160Zm320-320v-320h320v320H520Zm80-80h160v-160H600v160Zm160 480v-80h80v80h-80ZM520-360v-80h80v80h-80Zm80 80v-80h80v80h-80Zm-80 80v-80h80v80h-80Zm80 80v-80h80v80h-80Zm80-80v-80h80v80h-80Zm0-160v-80h80v80h-80Zm80 80v-80h80v80h-80Z',
    },
    priority: 45,
    render: (context: Record<string, unknown>) => ({ component: CodesPanel, props: { context } }),
  });

  Veyop.hooks?.registerSlot?.(
    'veyo_designer_inspector_before_arrange',
    'qr-barcode/edit-panel',
    (context) => {
      const item = context.item as CodeDesignItem | null;
      if (!isCodeItem(item) || typeof context.update !== 'function') return null;
      return {
        component: CodeEditPanel,
        props: {
          item,
          update: context.update as (patch: Record<string, unknown>) => void,
          ...colorProps(),
        },
      };
    },
    20,
  );

  Veyop.hooks?.whenReady?.('designer', (payload) => {
    const config = (payload as { config?: { brandPalette?: unknown; settings?: Record<string, unknown> } })?.config;
    const policy = normalizeColorPolicy(config?.settings?.color_picker_mode, config?.brandPalette);
    colorPickerMode = policy.mode;
    brandColors = policy.colors;
  });
}

function boot(): void {
  const w = window as unknown as { Veyop?: VeyopFacade };
  const tryRegister = () => {
    if (w.Veyop) registerOnVeyop(w.Veyop);
  };

  w.Veyop?.hooks?.on?.('veyop:designer:ready', tryRegister);
  tryRegister();

  const start = Date.now();
  const tick = () => {
    tryRegister();
    if (registered || Date.now() - start > 5000) return;
    setTimeout(tick, 50);
  };
  tick();
}

boot();
